
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { updateConstraint } from '@/Services/ProgrammationManagerService'
import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { RelationDescription } from '../Relations/RelationTypeSelector'
import { updateConstraintsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import Checkbox from '@/Components/SvgImages/Checkbox'

import './ConstraintItem.scss'

interface Props {
  constraint: any
  subTask?: any
  onSelect: () => void
}

const ConstraintItem = ({ constraint, subTask, onSelect }: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { tasks, relations } = useSelector((state: any) => state.ProgrammationReducer)

  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const familiesOptions = [
    { _id: 'general', name: t('CONSTRAINT_CREATOR:FamilyGeneral') },
    { _id: 'coordination', name: t('CONSTRAINT_CREATOR:FamilyCoordination') },
    { _id: 'resource', name: t('CONSTRAINT_CREATOR:FamilyResource') },
    { _id: 'time', name: t('CONSTRAINT_CREATOR:FamilyTime') },
    { _id: 'space', name: t('CONSTRAINT_CREATOR:FamilySpace') },
    { _id: 'design', name: t('CONSTRAINT_CREATOR:FamilyDesign') },
    { _id: 'logistic', name: t('CONSTRAINT_CREATOR:FamilyLogistic') },
  ]

  const [ checked, setChecked ] = useState(constraint.checked)

  const task = subTask || itemOpened
  const relationId = constraint.relation?._id || constraint.relation
  const relation = relations.find((item: any) => `${item._id}` === `${relationId}`)
  const dependencyTask = relation
    ? tasks.find((item: any) => `${item._id}` === `${relation.from_task}`)
    : null

  const getDependencyLag = () => {

    const lag = Number(relation?.lag) || 0

    if (!lag) return ''

    const days = Math.abs(lag)
    const key = lag > 0
      ? (days === 1 ? 'DependencyDelayOne' : 'DependencyDelayMany')
      : (days === 1 ? 'DependencyAdvanceOne' : 'DependencyAdvanceMany')

    return t(`CONSTRAINT_ITEM:${key}`).replace('{{days}}', `${days}`)
  }

  const renderDependency = () => {

    if (!relation || !dependencyTask) {

      return (
        <div className="constraint-item-name-text">
          {t('CONSTRAINT_ITEM:DependencyConstraint')}
        </div>
      )
    }

    const dependencyTaskName = dependencyTask.name || t('RELATIONS_CREATOR:Nameless')
    const eventKey = relation.type?.startsWith('start_to_')
      ? 'DependencyRequiresStart'
      : 'DependencyRequiresEnd'

    return (
      <div className="constraint-item-dependency">
        <div className="constraint-item-dependency-heading">
          <span className="constraint-item-dependency-badge">
            {t('CONSTRAINT_ITEM:Dependency')}
          </span>
          <strong>{dependencyTaskName}</strong>
        </div>
        <div className="constraint-item-dependency-requirement">
          <RelationDescription
            description={t(`CONSTRAINT_ITEM:${eventKey}`)}
            originName={dependencyTaskName}
          />
          {getDependencyLag()}.
        </div>
      </div>
    )
  }

  const handleUpdate = async (constraint: any) => {

    setEditingItem(true)

    const [ error, data ] = await updateConstraint(task._id, constraint)

    setEditingItem(false)

    if (error) {

      // TODO: show error
      return
    }

    if (!subTask) {

      patchItemOpened({
        restrictions: itemOpened.restrictions.map((c: any) => c._id === constraint._id ? constraint : c)
      })
    } else {
      const subTasks = itemOpened.subTasks

      subTasks.forEach((subTask: any) => {

        if (subTask._id === task._id) {

          subTask.restrictions = subTask.restrictions.map((c: any) => c._id === constraint._id ? constraint : c)
        }
      })

      patchItemOpened({
        subTasks
      })
    }

    dispatch(updateConstraintsAction({ task: data.task, parentTask: data.parentTask }))
  }

  const handleToggleCheckbox = (checked: boolean) => {

    setChecked(checked)

    handleUpdate({
      ...constraint,
      checked,
    })
  }

  return (
    <div className='constraint-item'>
      <div className="constraint-item-header">
        <div className="constraint-item-header-actions">
          <button
            className="constraint-item-button"
            onClick={() => handleToggleCheckbox(!constraint.checked)}
            disabled={!constraint.editable}
          >
            <Checkbox
              height={18}
              width={18}
              checked={checked}
              disabled={!!constraint.relation}
            />
          </button>
        </div>
        <div className={`constraint-item-name${constraint.relation ? ' dependency' : ''}`}
          onClick={onSelect}
        >
          {!constraint.relation && (
            <div className={`constraint-item-family-badge constraint-item-family-badge-${constraint.family}`}>
              {familiesOptions.find((f: any) => f._id === constraint.family)?.name}
            </div>
          )}
          {!constraint.relation && (
            <div className="constraint-item-name-text">
              {constraint.description}
            </div>
          )}
          {constraint.relation && renderDependency()}
        </div>
      </div>
    </div>
  )
}

export default ConstraintItem
